import { Link, useNavigate } from "react-router-dom";
import SEO, { backorderFaqSchema } from "@/components/SEO";
import {
  ArrowRight,
  ChevronRight,
  FileText,
  Radio,
  Package,
  CheckCircle2,
  Truck,
  ShieldCheck,
  Clock,
  Network,
  Phone,
  Mail,
  Building2,
  Sparkles,
  ArrowDown,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import LiveInventoryEmbed from "@/components/LiveInventoryEmbed";

/**
 * Hospital-focused explainer for the Sterile Mate "we hunt your backorders" workflow.
 * Linked from the Backordered Item Alert email and from the main How-It-Works page.
 *
 * Route: /how-it-works/backorder-sourcing
 *
 * Visual system: dark navy (#0c1939) backdrop, cyan/amber accents, glass-morphism
 * cards — matches SolutionsOverviewPage and the landing-page Quick Quote section.
 */

// Pexels stock photos — diverse healthcare + supply-chain imagery for the
// per-step illustrations. All photos verified to match brief.
const HERO_BG =
  "https://images.pexels.com/photos/4481256/pexels-photo-4481256.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=1920&h=900";
const HERO_TEAM =
  "https://images.pexels.com/photos/5452193/pexels-photo-5452193.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=900&h=720";

const STEPS = [
  {
    n: 1,
    icon: FileText,
    title: "Your team submits a backorder request",
    desc: "Supply Chain, OR, EVS, or Bio Med tells us what you need — SKU, quantity, urgency. Two minutes through the AskIsaac portal, an email, or a phone call.",
    bullets: [
      "Item / SKU / manufacturer number",
      "Quantity needed and urgency",
      "Special handling notes (cold chain, sterile, expiry window)",
    ],
    accent: "cyan",
    image: "https://images.pexels.com/photos/4226215/pexels-photo-4226215.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=900&h=600",
    alt: "Hospital supply chain manager filling a backorder request on a clipboard",
  },
  {
    n: 2,
    icon: Radio,
    title: "We broadcast to our nationwide supplier network",
    desc: "Your request goes out simultaneously to dozens of vetted suppliers, distributors, and secondary-market sources — blind-bid so they don't see each other's offers.",
    bullets: [
      "50+ verified U.S. suppliers contacted in parallel",
      "Cold-call follow-up on critical items",
      "Audit trail of every supplier touched",
    ],
    accent: "violet",
    image: "https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=900&h=600",
    alt: "Diverse procurement team coordinating supplier outreach around laptops and tablets",
  },
  {
    n: 3,
    icon: Package,
    title: "Competitive bids come back — usually within hours",
    desc: "Suppliers submit price-per-unit, available quantity, lot/expiry data, and earliest ship date. We aggregate and de-dupe for you.",
    bullets: [
      "Tiered pricing surfaced (volume discounts visible)",
      "Lead-time and origin warehouse on every bid",
      "Lot, expiry, and country-of-origin captured up-front",
    ],
    accent: "amber",
    image: "https://images.pexels.com/photos/5860937/pexels-photo-5860937.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=900&h=600",
    alt: "Organized warehouse aisle with cardboard boxes ready for fulfillment",
  },
  {
    n: 4,
    icon: CheckCircle2,
    title: "You get a single, transparent estimate",
    desc: "Sterile Mate presents the best matching offer — including a tiered estimate (e.g. 1–49 units / 50+ units / volume cap) — with the lead time, fulfillment terms, and any market-availability caveats clearly spelled out.",
    bullets: [
      "Unit price + tier breaks shown clearly",
      "Lead-time and PO terms on the estimate",
      "PO can be uploaded later — we secure first if you give the green light",
    ],
    accent: "emerald",
    image: "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=900&h=600",
    alt: "Procurement team reviewing a transparent quote and price-break charts at a meeting table",
  },
  {
    n: 5,
    icon: Truck,
    title: "We secure, invoice, and ship — direct to your facility",
    desc: "Once you approve, the supply gets pulled and shipped to your dock with full tracking. Sterile Mate handles every logistics step and invoices you on standard hospital terms — no markup surprises.",
    bullets: [
      "Air-freight and same-day options on critical SKUs",
      "Standard hospital terms (Net 30 / EFT / PCard)",
      "Full chain-of-custody and shipment tracking",
    ],
    accent: "rose",
    image: "https://images.pexels.com/photos/4391470/pexels-photo-4391470.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=900&h=600",
    alt: "Delivery driver handing a clinical shipment to a hospital receiving dock",
  },
];

// Tailwind-safe per-step palette. Static keys keep purge happy.
const ACCENT = {
  cyan: {
    chip: "bg-cyan-500/15 text-cyan-900 ring-cyan-400/30",
    border: "border-cyan-400/30",
    glow: "from-cyan-500/40 to-blue-500/0",
    badge: "bg-cyan-500 text-slate-900",
    bar: "bg-gradient-to-b from-cyan-400 via-cyan-500 to-cyan-700",
  },
  violet: {
    chip: "bg-violet-500/15 text-violet-200 ring-violet-400/30",
    border: "border-violet-400/30",
    glow: "from-violet-500/40 to-fuchsia-500/0",
    badge: "bg-violet-500 text-slate-900",
    bar: "bg-gradient-to-b from-violet-400 via-violet-500 to-purple-700",
  },
  amber: {
    chip: "bg-amber-500/15 text-amber-900 ring-amber-400/30",
    border: "border-amber-400/30",
    glow: "from-amber-500/40 to-orange-500/0",
    badge: "bg-amber-500 text-slate-900",
    bar: "bg-gradient-to-b from-amber-300 via-amber-500 to-orange-600",
  },
  emerald: {
    chip: "bg-emerald-500/15 text-emerald-200 ring-emerald-400/30",
    border: "border-emerald-400/30",
    glow: "from-emerald-500/40 to-teal-500/0",
    badge: "bg-emerald-500 text-slate-900",
    bar: "bg-gradient-to-b from-emerald-300 via-emerald-500 to-teal-700",
  },
  rose: {
    chip: "bg-rose-500/15 text-rose-200 ring-rose-400/30",
    border: "border-rose-400/30",
    glow: "from-rose-500/40 to-red-500/0",
    badge: "bg-rose-500 text-slate-900",
    bar: "bg-gradient-to-b from-rose-300 via-rose-500 to-red-700",
  },
};

const STAT_PILLS = [
  { icon: Clock, label: "Bid turnaround", value: "Hours, not days" },
  { icon: Network, label: "Supplier reach", value: "50+ U.S. sources" },
  { icon: ShieldCheck, label: "Hospital-ready terms", value: "Net 30 / EFT / PCard" },
];

const WHY_BULLETS = [
  { title: "Single point of accountability", desc: "One contact for sourcing, ETA updates, and shipment tracking — no chasing distributors." },
  { title: "Transparent secondary-market pricing", desc: "We disclose when an item is sourced from secondary inventory and why the price reflects that." },
  { title: "Hospital procurement terms", desc: "Standard PO workflow, Net 30 / EFT / PCard — no marketplace surcharges or transaction fees." },
  { title: "Audit trail for every quote", desc: "Every supplier contact, bid, and decision is logged for your compliance and contracting teams." },
];

export default function BackorderSourcingPage() {
  const navigate = useNavigate();
  const goToQuickQuote = () => {
    navigate("/");
    // Poll up to ~3s for the homepage's Quick Quote section to mount, then scroll
    let attempts = 0;
    const tick = () => {
      attempts += 1;
      const el = document.getElementById("quick-quote");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts < 30) {
        setTimeout(tick, 100);
      }
    };
    setTimeout(tick, 100);
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="backorder-sourcing-page">
      <SEO
        canonical="/how-it-works/backorder-sourcing"
        title="Hospital Supply Backorder Sourcing Service — Sterile Mate"
        description="Stop losing cases to allocated SKUs. Sterile Mate hunts backordered hospital supplies across a nationwide distributor network — 2-hour RFQ turnaround, same-day dispatch, TX/LA/AL/MS coverage. Non-GPO alternative for surgical, sterile processing, EVS, and water systems."
        keywords={[
          "hospital supply backorder resolution",
          "allocated SKU sourcing service",
          "medical supply backorder alternative",
          "surgical supply backorder Texas",
          "sterile processing backorder solution",
          "same-day hospital dispatch",
          "hospital procurement emergency",
        ]}
        schema={backorderFaqSchema}
      />
      <SiteHeader />

      {/* ───────── HERO (dark navy) ───────── */}
      <section className="relative overflow-hidden bg-[#070d22] text-white">
        {/* Background photo */}
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_BG}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#070d22] via-[#0c1939]/95 to-[#0c1939]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(245,158,11,0.15),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(14,165,233,0.18),transparent_55%)]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-14 md:py-24">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 text-sm">
            <Link to="/" className="text-cyan-200/80 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-cyan-400/50" />
            <Link to="/how-it-works" className="text-cyan-200/80 hover:text-white transition-colors">How It Works</Link>
            <ChevronRight className="w-4 h-4 text-cyan-400/50" />
            <span className="text-amber-300 font-semibold">Backorder Sourcing</span>
          </div>

          <div className="grid lg:grid-cols-[1fr_440px] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-500/15 ring-1 ring-amber-400/30 backdrop-blur px-3 py-1.5 rounded-full mb-5">
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-200">
                  For Hospitals · Procurement &amp; Supply Chain
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-5 leading-[1.05] tracking-tight text-white">
                When a SKU goes <span className="text-amber-300">backordered</span>,
                <br className="hidden md:inline" />
                we go to work <span className="text-cyan-300">— so your O.R. doesn't stop.</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
                One request from you. Dozens of vetted sources contacted in parallel.
                A single transparent estimate back — and shipment direct to your dock,
                on standard hospital terms.
              </p>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={goToQuickQuote}
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold shadow-lg shadow-amber-500/30"
                  data-testid="bs-cta-submit-request"
                >
                  Submit a Backorder Request
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-cyan-400/30 bg-white/5 text-cyan-100 hover:bg-cyan-500/10 hover:text-white hover:border-cyan-400/60 font-semibold backdrop-blur"
                  data-testid="bs-cta-talk-to-us"
                >
                  <a href="mailto:orders@sterilemate.com">
                    <Mail className="w-4 h-4 mr-2" /> Email your request
                  </a>
                </Button>
              </div>

              {/* Stat pills */}
              <div className="grid sm:grid-cols-3 gap-3 mt-10">
                {STAT_PILLS.map((s) => (
                  <div
                    key={s.label}
                    className="bg-white/[0.04] backdrop-blur ring-1 ring-white/[0.08] rounded-xl p-4 hover:ring-cyan-400/30 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-500/15 ring-1 ring-cyan-400/25 flex items-center justify-center">
                        <s.icon className="w-4 h-4 text-cyan-300" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-cyan-200/80">
                          {s.label}
                        </p>
                        <p className="text-sm font-bold text-white">{s.value}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero photo card */}
            <div className="hidden lg:block relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-amber-500/20 via-cyan-500/10 to-transparent blur-2xl rounded-3xl" />
              <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-2xl shadow-cyan-900/40">
                <img
                  src={HERO_TEAM}
                  alt="Hospital supply chain and clinical team coordinating on a backorder request"
                  className="w-full h-[440px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c1939] via-[#0c1939]/40 to-transparent" />
                <div className="absolute left-5 right-5 bottom-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300 mb-1">
                    On the dock by next business day
                  </p>
                  <p className="text-sm text-white/90 leading-snug">
                    From the moment a backordered SKU lands in our queue, we own the hunt —
                    so your team can keep cases on the schedule.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Amber horizon stripe at bottom of hero — Sterile Mate signature */}
        <div className="relative z-10 h-1 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
      </section>

      {/* ───────── LIVE BACKORDER LIST — right after the hero, next to the CTA ───────── */}
      <section id="live-inventory" className="relative bg-gradient-to-b from-white via-slate-100 to-white py-16 md:py-20 border-y-2 border-slate-200">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,0.05),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4">
          <LiveInventoryEmbed theme="light" />
        </div>
      </section>

      {/* ───────── 5-STEP FLOW ───────── */}
      <section className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-amber-900 bg-amber-200 ring-1 ring-amber-400 shadow-sm px-3 py-1.5 rounded-full mb-4">
            The 5-Step Hospital Workflow
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-4 leading-tight">
            From <span className="text-rose-700">"we can't get it"</span> to{" "}
            <span className="text-emerald-700">"it's on the dock"</span>
          </h2>
          <p className="text-lg text-slate-800 leading-relaxed">
            Every step is auditable, every supplier is vetted, every estimate is transparent.
            Here's exactly what happens after you tell us a SKU is backordered.
          </p>
        </div>

        <div className="space-y-6">
          {STEPS.map((step, idx) => {
            const a = ACCENT[step.accent];
            const Icon = step.icon;
            const reverse = idx % 2 === 1;
            return (
              <div
                key={step.n}
                className={`group relative bg-white rounded-2xl ring-1 shadow-sm ${a.border} hover:ring-2 hover:shadow-2xl transition-all overflow-hidden`}
                data-testid={`bs-step-${step.n}`}
              >
                {/* Decorative corner glow */}
                <div className={`absolute -top-20 ${reverse ? "-left-20" : "-right-20"} w-64 h-64 bg-gradient-to-br ${a.glow} blur-3xl pointer-events-none opacity-70 group-hover:opacity-100 transition-opacity`} />

                <div className={`relative grid md:grid-cols-[80px_1fr_360px] ${reverse ? "md:grid-cols-[360px_1fr_80px]" : ""} gap-0`}>
                  {/* Step number bar */}
                  <div className={`hidden md:block ${a.bar} ${reverse ? "md:order-3" : ""}`}>
                    <div className="h-full flex flex-col items-center justify-center p-4">
                      <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-900/90 mb-1 rotate-180" style={{ writingMode: "vertical-rl" }}>
                        Step 0{step.n}
                      </p>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`p-7 md:p-9 ${reverse ? "md:order-2" : ""}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-xl ${a.badge} flex items-center justify-center shadow-lg`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${a.chip} ring-1 px-2.5 py-1 rounded-full`}>
                        Step 0{step.n}
                      </span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-3 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-slate-800 leading-relaxed mb-5 text-[15px]">
                      {step.desc}
                    </p>
                    <ul className="space-y-2.5">
                      {step.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-sm text-slate-800">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Per-step photo */}
                  <div className={`relative h-56 md:h-auto md:min-h-[280px] overflow-hidden ${reverse ? "md:order-1" : ""}`}>
                    <img
                      src={step.image}
                      alt={step.alt}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-${reverse ? "r" : "l"} from-slate-100/70 via-slate-100/10 to-transparent`} />
                  </div>
                </div>

                {idx < STEPS.length - 1 && (
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-20">
                    <div className="w-9 h-9 rounded-full bg-amber-500 ring-4 ring-white flex items-center justify-center shadow-lg">
                      <ArrowDown className="w-4 h-4 text-slate-900" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ───────── WHY HOSPITALS CHOOSE US ───────── */}
      <section className="relative bg-white py-16 md:py-20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(34,211,238,0.08),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-10 items-start">
            <div>
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-900 bg-cyan-200 ring-1 ring-cyan-400 shadow-sm px-3 py-1.5 rounded-full mb-4">
                Why hospitals choose us
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-4">
                You stay <span className="text-cyan-900">operational</span>.<br />
                We stay <span className="text-amber-900">accountable</span>.
              </h2>
              <p className="text-slate-800 leading-relaxed text-[15px]">
                We're not a marketplace that walks away after the order. We own the hunt,
                the bid, the logistics, and the invoice — and we're on the line if something
                slips.
              </p>
              <div className="mt-8 hidden lg:block">
                <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10">
                  <img
                    src="https://images.pexels.com/photos/6129499/pexels-photo-6129499.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=900&h=520"
                    alt="Diverse healthcare team smiling in a hospital corridor"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {WHY_BULLETS.map((c, i) => (
                <div
                  key={c.title}
                  className="group rounded-2xl bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-md ring-1 ring-slate-300 hover:ring-cyan-400/40 p-5 transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                      ["bg-cyan-500/15 text-cyan-900", "bg-amber-500/15 text-amber-900", "bg-emerald-500/15 text-emerald-700", "bg-violet-500/15 text-violet-300"][i]
                    }`}>
                      <Layers className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-[15px] mb-1.5 leading-snug">{c.title}</p>
                      <p className="text-[13px] text-slate-800 leading-relaxed">{c.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────── FINAL CTA ───────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=1920&h=600"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-900" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(245,158,11,0.18),transparent_60%)]" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-200 ring-1 ring-amber-400 shadow-sm px-3 py-1.5 rounded-full mb-5">
            <Sparkles className="w-3.5 h-3.5 text-amber-900" />
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-900">
              Backorders &middot; Same-day response
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold mb-4 leading-tight">
            A SKU you can't find <span className="text-amber-900">right now</span>?
          </h2>
          <p className="text-slate-800 mb-8 text-lg max-w-2xl mx-auto leading-relaxed">
            Send it over. We'll have the broadcast out within an hour and a transparent
            estimate back to you before the end of the next business day on most clinical SKUs.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={goToQuickQuote}
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold shadow-xl shadow-amber-500/30"
              data-testid="bs-cta-final-submit"
            >
              Submit a Backorder Request
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-cyan-400/30 bg-white/5 text-cyan-100 hover:bg-cyan-500/10 hover:text-slate-900 hover:border-cyan-400/60 font-semibold backdrop-blur"
              data-testid="bs-cta-final-call"
            >
              <a href="tel:2814563458">
                <Phone className="w-4 h-4 mr-2" /> (281) 456-3458
              </a>
            </Button>
          </div>
          <p className="text-xs text-slate-800 mt-7">
            Sterile Mate LLC · Backorder Sourcing Desk · george.broughton@sterilemate.com
          </p>
        </div>
      </section>
    </div>
  );
}
